<template>
    <section class="cost-section">
        <!-- Разбивка по слотам -->
        <div v-if="frame.costBreakdown && frame.costBreakdown.length" class="cost-breakdown">
            <!-- Выбор времени по дням недели -->
            <div class="weekday-time-section">
                <WeekdayTimeSelector :time-settings="frame.timeSettings || []" :hourly-price="getHourlyPrice()"
                    :active-periods="frame.periods || []" @update:time-settings="updateTimeSettings($event)" />
            </div>

            <!-- Старая разбивка (можно скрыть) -->
            <div class="breakdown-grid" style="display: none;">
                <div v-for="(slot, index) in frame.costBreakdown" :key="index" class="breakdown-item">
                    <div class="slot-date">{{ slot.day }}</div>
                    <div class="slot-duration">{{ slot.hours }}ч</div>
                    <div class="slot-cost">{{ formatPrice(slot.partialCost) }}</div>
                    <div v-if="slot.timeSlot" class="slot-time">{{ slot.timeSlot }}</div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import WeekdayTimeSelector from '../WeekdayTimeSelector.vue';

export default {
    components: {
        WeekdayTimeSelector
    },
    props: ['frame', 'tariffs', 'campaignPeriod'],
    emits: ['update:frame'],
    data() {
        return {};
    },
    methods: {
        formatPrice(n) {
            const num = Number(n || 0);
            return new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: 'KZT',
                currencyDisplay: 'symbol',
                maximumFractionDigits: 2,
            }).format(num);
        },
        getTariffName() {
            if (!this.tariffs || !this.frame.selectedTariffId) return '';
            const tariff = this.tariffs.find(t => t.id == this.frame.selectedTariffId);
            if (!tariff) return '';
            const hourlyPrice = Number(tariff.price) / 24;
            return `${this.formatPrice(hourlyPrice)}/час`;
        },
        getDaysCount() {
            if (!this.frame.periods || !this.frame.periods.length) return 0;
            let total = 0;
            this.frame.periods.forEach(p => {
                if (p.start && p.end) {
                    const start = new Date(p.start);
                    const end = new Date(p.end);
                    const days = Math.max(1, Math.floor((end - start) / (24 * 60 * 60 * 1000)) + 1);
                    total += days;
                }
            });
            return total;
        },
        getHourlyPrice() {
            if (!this.frame.selectedTariffId || !this.tariffs) return 0;
            const tariff = this.tariffs.find(t => t.id == this.frame.selectedTariffId);
            if (!tariff) return 0;
            return Number(tariff.price) / 24;
        },
        updateTimeSettings(timeSettings) {
            // Обновляем настройки времени напрямую без привязки к конкретным датам
            this.$emit('update:frame', {
                ...this.frame,
                timeSettings: timeSettings || []
            });
        },
        getTotalHoursFromTimeSettings() {
            // Подсчитываем общее количество часов из настроек времени
            if (!this.frame.timeSettings || this.frame.timeSettings.length === 0) {
                return 0;
            }

            let totalHours = 0;
            this.frame.timeSettings.forEach(ts => {
                const startHour = Math.floor(ts.startTime / (60 * 60 * 1000));
                const endHour = Math.floor(ts.endTime / (60 * 60 * 1000));
                totalHours += endHour - startHour;
            });

            return totalHours;
        },
        getTotalCostFromTimeSettings() {
            // Подсчитываем общую стоимость на основе настроек времени
            const totalHours = this.getTotalHoursFromTimeSettings();
            const hourlyPrice = this.getHourlyPrice();
            return totalHours * hourlyPrice;
        },
        getActiveDaysCount() {
            // Подсчитываем количество активных дней недели
            if (!this.frame.timeSettings || this.frame.timeSettings.length === 0) {
                return 0;
            }

            const activeDays = new Set(this.frame.timeSettings.map(ts => ts.day));
            return activeDays.size;
        },
    },
};
</script>

<style scoped>
.cost-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
}

.cost-main {
    display: flex;
    align-items: baseline;
    gap: 12px;
}

.cost-amount {
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.025em;
}

.cost-period {
    font-size: 14px;
    font-weight: 500;
    color: #64748b;
    background: #e2e8f0;
    padding: 4px 8px;
    border-radius: 6px;
}

.cost-rate {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
    text-align: right;
    line-height: 1.4;
}

.time-warnings {
    margin-bottom: 12px;
    padding: 8px 12px;
    background: #fef3c7;
    border: 1px solid #f59e0b;
    border-radius: 6px;
}

.warning-item {
    font-size: 12px;
    color: #92400e;
    font-weight: 500;
    margin-bottom: 4px;
}

.warning-item:last-child {
    margin-bottom: 0;
}

.hour-checkbox-wrapper {
    margin-bottom: 8px;
}

.hour-checkbox-wrapper:last-child {
    margin-bottom: 0;
}

.breakdown-item {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 12px;
    align-items: center;
    padding: 10px 12px;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 8px;
    border: 1px solid rgba(226, 232, 240, 0.5);
    transition: all 0.2s ease;
}

.breakdown-item:hover {
    background: rgba(255, 255, 255, 0.9);
    border-color: #cbd5e1;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.slot-date {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
}

.slot-duration {
    font-size: 13px;
    color: #475569;
    font-weight: 600;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
}

.slot-cost {
    font-size: 13px;
    color: #059669;
    font-weight: 700;
}

.slot-time {
    grid-column: 1 / -1;
    font-size: 11px;
    color: #94a3b8;
    margin-top: 4px;
    font-style: italic;
}

.weekday-time-section {
    margin-top: 16px;
}

/* Адаптивность */
@media (max-width: 640px) {
    .cost-section {
        padding: 16px;
    }

    .cost-summary {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .cost-rate {
        text-align: left;
    }

    .breakdown-item {
        grid-template-columns: 1fr;
        gap: 6px;
        text-align: center;
    }

    .slot-duration,
    .slot-cost {
        justify-self: center;
    }
}
</style>
